// Timeslot short codes (rel values) -> 'Delivery Session' value sent to OMNI.
// OMNI's contract uses the old 3-session vocabulary (MORNING/STANDARD/EVENING),
// so the new 4-slot picker maps DOWN to it: AFTERNOON is the old PM "STANDARD"
// slot. Values are UPPER snake_case (no spaces) to match what OMNI expects.
// Keep this in sync with the restore mapping in cart-ajax.liquid (_labels).
const TIMESLOT_LABELS = {
    EARLY_MORNING: 'EARLY_MORNING',
    MORNING: 'MORNING',
    AFTERNOON: 'STANDARD',   // legacy/bfcache: a page still holding the raw slot code
    STANDARD: 'STANDARD',    // the session input now stores the OMNI label directly
    EVENING: 'EVENING'
};

// Surcharge product variants added to cart per timeslot (SCIPL-208).
// EARLY_MORNING = RM10, EVENING = RM5 but only for a same-day order placed
// between 2 PM and 5 PM (TEC-1210). Free slots map to nothing.
// ponytail: variant ids hard-coded from staging store; must be QA'd on staging theme.
const TIMESLOT_SURCHARGE_VARIANTS = {
    EARLY_MORNING: 43756601901119,
    EVENING: 43756601933887
};

// TEC-1210: EVENING is always on offer, but it only COSTS anything for a
// same-day order placed between 2 PM and 5 PM. Next-day-onward evenings are
// free, and so are same-day evenings picked outside that window.
//
// Both helpers read the SHOP's clock (server-rendered in main-cart.liquid), not
// the shopper's device. If either value is missing we treat the pick as free: a
// missed charge is recoverable, silently billing RM5 on a free slot is not.
//
// Both values are captured at page load, exactly like every other cutoff in the
// picker. That is deliberate: the fee LABEL and the real CHARGE then read the
// same number, so they can never disagree. The cost is that a shopper sitting on
// the page across 2 PM keeps the pricing they loaded with until they reload.
function isSameDayDelivery(dateStr) {
    const today = window.STORE_TODAY_DDMMYYYY;
    if (!today || !dateStr) return false;
    return String(dateStr).trim() === String(today).trim();
}

// ---------------------------------------------------------------------------
// THE chargeable window for EVENING. This is the ONLY place it is defined.
// cart-ajax-header.liquid's picker calls isEveningFeeWindow() rather than
// repeating the hours, so the +RM5 label and the real charge cannot be set to
// different times. To shift the window (or widen it for QA), edit Theme
// settings -> "Evening Delivery Fee" — no code change, no deploy. The constants
// below are only the fallback when that setting is blank or malformed.
// ---------------------------------------------------------------------------
const EVENING_FEE_DEFAULT_FROM = 1400;   // 2:00 PM
const EVENING_FEE_DEFAULT_TO   = 1700;   // 5:00 PM

// Parse "2:00 PM" -> 1400, the same hours*100+minutes form get24Hr() produces in
// cart-ajax-header.liquid. Returns null when the setting is blank or malformed,
// so a typo in the admin falls back to the documented window rather than
// switching the fee off (lost revenue) or leaving it on all day (shoppers
// charged when they should not be).
function parseSettingHHMM(text) {
    const m = String(text == null ? '' : text).trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp])\.?[Mm]?\.?$/);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (h < 1 || h > 12 || min > 59) return null;
    if (h === 12) h = 0;
    if (m[3].toLowerCase() === 'p') h += 12;
    return h * 100 + min;
}

const _feeFrom = parseSettingHHMM(window.EVENING_FEE_START);
const _feeTo = parseSettingHHMM(window.EVENING_FEE_END);
if (_feeFrom === null || _feeTo === null) {
    console.warn('Evening fee window setting unreadable; falling back to 2:00 PM - 5:00 PM.',
        window.EVENING_FEE_START, window.EVENING_FEE_END);
}
const EVENING_FEE_FROM_HHMM = _feeFrom === null ? EVENING_FEE_DEFAULT_FROM : _feeFrom;
const EVENING_FEE_TO_HHMM = _feeTo === null ? EVENING_FEE_DEFAULT_TO : _feeTo;

function isEveningFeeWindow() {
    const now = parseInt(window.STORE_NOW_HHMM, 10);
    if (!Number.isFinite(now)) return false;
    return now >= EVENING_FEE_FROM_HHMM && now <= EVENING_FEE_TO_HHMM;
}

// The surcharge variant actually owed for a slot on a given delivery date.
// Single source of truth for "does this pick cost anything".
function surchargeVariantFor(timeslotCode, deliveryDate) {
    if (timeslotCode === 'EVENING') {
        if (!isSameDayDelivery(deliveryDate)) return null;   // next day onward: free
        if (!isEveningFeeWindow()) return null;              // same day, outside 2-5 PM: free
    }
    return TIMESLOT_SURCHARGE_VARIANTS[timeslotCode] || null;
}

const postCartJson = (url, payload) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(payload)
});

// TEC-1003: back up the picked date/session to bt-infra (Redis, keyed by cart
// token) so OMNI can recover them for orders where the cart attributes never
// reached the order. Called on each Delivery Session click (cart-ajax-footer).
// Fire-and-forget: never blocks the UI and never shows an error to the shopper.
// The relative URL goes through the OMNI Integration app proxy, which adds
// `shop` itself. The cart token is read from /cart.js once and cached.
const CART_DELIVERY_BACKUP_URL = '/apps/customer-data/cart-store';
let cartTokenPromise = null;

function saveCartDeliveryBackup(deliveryDate, deliverySession) {
    if (!deliveryDate || !deliverySession) return;
    if (!cartTokenPromise) {
        cartTokenPromise = fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
            .then((r) => (r.ok ? r.json() : null))
            .then((cart) => (cart && cart.token) || null)
            .catch(() => null);
    }
    cartTokenPromise.then((cartToken) => {
        if (!cartToken) { cartTokenPromise = null; return; }   // retry the read next click
        return fetch(CART_DELIVERY_BACKUP_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartToken, deliveryDate, deliverySession }),
            keepalive: true
        });
    }).catch((e) => console.warn('Cart delivery backup failed', e));
}

// Cart sync worker. Runs once, on Checkout click (SCIPL-208): reconciles the
// timeslot surcharge and writes delivery props/attributes to the cart, then the
// caller redirects to /checkout. No longer fired on each date/session pick.
//
// Single-flight guard: the checkout button in the cart drawer isn't #addcart, so
// it isn't disabled during a run — a fast double-click could otherwise launch two
// concurrent syncs whose /cart mutations interleave. Concurrent calls coalesce
// into the one in-flight run.
let cartSyncInFlight = null;
function performCartSync(temp_date_selected, noteValue) {
    if (cartSyncInFlight) return cartSyncInFlight;
    cartSyncInFlight = _doCartSync(temp_date_selected, noteValue).finally(() => { cartSyncInFlight = null; });
    return cartSyncInFlight;
}

async function _doCartSync(temp_date_selected, noteValue) {
    const addcartButton = document.getElementById("addcart");

    const deliverySessionInput = document.getElementById("delivery-session-input");

    const deliveryDate = temp_date_selected;
    // Read the live .value (jQuery .val() sets the property, not the attribute).
    const timeslotCode = (deliverySessionInput && deliverySessionInput.value) || 'MORNING';
    const deliverySession = TIMESLOT_LABELS[timeslotCode] || 'MORNING';
    const desiredVariant = surchargeVariantFor(timeslotCode, deliveryDate);
    const allSurchargeVariants = Object.values(TIMESLOT_SURCHARGE_VARIANTS);
    // SCIPL-226: whether the cart ended in a checkout-safe state. Only a paid slot
    // whose surcharge didn't land correctly flips this false. Returned to the caller
    // so it can block the redirect. Defaults true (free slots are always safe).
    let surchargeOk = true;
    // Set once the surcharge is verified correct; after that, an unrelated error
    // (a flaky property write, attributes update) must NOT block a correctly-priced
    // checkout. Only an error BEFORE verification leaves the price unconfirmed.
    let surchargeConfirmed = false;

    if (addcartButton) {
        addcartButton.disabled = true;
        addcartButton.textContent = "Loading...";
    }
    $("#loading-spinner").show();
    // Block the qty +/- and remove controls while the cart mutates (CSS below),
    // so a click mid-sync can't race the cart or act on a key about to change.
    document.body.classList.add('cart-syncing');

    try {
        // 1. Write delivery date/session at the ORDER level (cart attributes) —
        //    OMNI's source of truth. NO per-line-item property writes: they added
        //    one serialized /cart/change.js per product (the checkout bottleneck)
        //    for data OMNI doesn't read. /cart/update.js also returns the full cart,
        //    so we reuse its response as the read for reconcile below — no separate
        //    /cart.js round-trip (SCIPL-208 latency).
        // SCIPL: write the message-card note in the SAME awaited request as the
        // delivery attributes, so the note is guaranteed committed BEFORE /checkout.
        // Previously the note relied on theme.js's separate fire-and-forget `change`
        // handler, which never fired for suggested-message picks and could be dropped
        // in the redirect race / cart throttling — the "missing note" bug. Only set
        // note when a string was actually passed, so older callers can't clobber it.
        const updatePayload = {
            attributes: {
                'Delivery Date': deliveryDate,
                'Delivery Session': deliverySession
            }
        };
        if (typeof noteValue === 'string') updatePayload.note = noteValue;

        const updateResponse = await postCartJson('/cart/update.js', updatePayload);

        if (!updateResponse.ok) {
            console.error('Cart attribute update failed', updateResponse.status);
            // Paid slot: never read the cart, price unconfirmed -> block. Free slot: no price risk.
            if (desiredVariant) { surchargeOk = false; return false; }
            return surchargeOk;
        }
        let cart = await updateResponse.json();

        // 2. Reconcile the surcharge IDEMPOTENTLY, by line key, against the cart
        //    update.js just returned. Keep exactly one desired-variant line at qty 1;
        //    zero every other surcharge line (dupes + stale slots). add.js only if
        //    none exists. reconcileOk tracks whether every mutation applied.
        let keptDesired = false;
        let reconcileOk = true;
        for (const line of cart.items) {
            if (!allSurchargeVariants.includes(line.variant_id)) continue;
            const isDesired = desiredVariant && line.variant_id === desiredVariant;
            if (isDesired && !keptDesired) {
                keptDesired = true;
                if (line.quantity !== 1) {
                    const r = await postCartJson('/cart/change.js', { id: line.key, quantity: 1 });
                    if (r.ok) cart = await r.json(); else reconcileOk = false;
                }
            } else {
                const r = await postCartJson('/cart/change.js', { id: line.key, quantity: 0 });
                if (r.ok) cart = await r.json(); else reconcileOk = false;
            }
        }
        if (desiredVariant && !keptDesired) {
            const addRes = await postCartJson('/cart/add.js', { id: desiredVariant, quantity: 1 });
            if (!addRes.ok) {
                reconcileOk = false;
                const err = await addRes.json().catch(() => ({}));
                // Most common cause: surcharge variant is inventory-tracked and sold out.
                // Set it to "Continue selling when out of stock" in Shopify admin.
                console.error('Timeslot surcharge add failed', desiredVariant, addRes.status, err.description || err.message || err);
            }
        }

        // SCIPL-226: a paid slot MUST end with EXACTLY its surcharge, qty 1, and no
        // other surcharge line. We don't re-read to check: the reconcile is
        // deterministic and the single-flight guard rules out concurrent mutation,
        // so if every mutation applied (reconcileOk) the end state IS one desired
        // line at qty 1 with all others zeroed. Any failed mutation on a paid slot
        // leaves the price unconfirmed -> block.
        if (desiredVariant && !reconcileOk) {
            surchargeOk = false;
            return false;
        }
        surchargeConfirmed = true;

    } catch (error) {
        console.error('Cart update error:', error);
        // Block ONLY if the surcharge was never verified (error during reconcile/add
        // on a paid slot => price unconfirmed). An error after confirmation leaves a
        // correctly-priced cart, so don't strand the shopper.
        if (desiredVariant && !surchargeConfirmed) surchargeOk = false;
    } finally {
        if (addcartButton) {
            addcartButton.disabled = false;
            addcartButton.textContent = "Checkout";
        }
        $("#loading-spinner").hide();
        document.body.classList.remove('cart-syncing');
    }
    return surchargeOk;
}

// SCIPL-208: drop an orphaned timeslot surcharge line.
// The surcharge line is auto-managed (added on Checkout click for a paid slot).
// If the shopper returns to the cart and removes every real product, that line
// can be left behind on its own — a cart holding a delivery fee and no item.
// On cart-page load, if ONLY surcharge lines remain, remove them so the cart
// reads as empty. This file is loaded only on the cart page (main-cart.liquid),
// so no page-type guard is needed.
async function removeOrphanTimeslotSurcharge() {
    const surchargeVariants = Object.values(TIMESLOT_SURCHARGE_VARIANTS);
    if (!surchargeVariants.length) return;

    let cart;
    try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        if (!res.ok) return;
        cart = await res.json();
    } catch (e) { return; }
    if (!cart || !Array.isArray(cart.items)) return;

    const surchargeLines = cart.items.filter(i => surchargeVariants.includes(i.variant_id));
    if (!surchargeLines.length) return;                                  // no surcharge line -> nothing to do
    const hasRealProduct = cart.items.some(i => !surchargeVariants.includes(i.variant_id));
    if (hasRealProduct) return;                                          // real product present -> keep the fee

    // Only surcharge lines remain: zero them, then reload so the cart renders empty.
    // After the reload surchargeLines is empty, so this returns early — no loop.
    const updates = {};
    surchargeLines.forEach(l => { updates[l.key] = 0; });
    try {
        const r = await postCartJson('/cart/update.js', { updates });
        if (r.ok) window.location.reload();
    } catch (e) { /* leave the cart untouched on failure */ }
}

// Deferred script: the DOM is already parsed when this runs.
removeOrphanTimeslotSurcharge();

// SCIPL-208: reflect the picked slot's delivery fee in the cart totals immediately.
// The surcharge line is only written to the cart on Checkout (performCartSync), so the
// server-rendered "Delivery fee"/Total otherwise lag the picker. This updates the DISPLAYED
// fee + total the moment a slot is chosen (or on load, from the restored slot). The real
// cart reconciles the surcharge at checkout, so the displayed total matches what's charged.
const TIMESLOT_SURCHARGE_AMOUNT = { EARLY_MORNING: 1000, EVENING: 500 }; // cents; free slots => 0
// EVENING's 500 applies to a same-day 2-5 PM pick only; see surchargeVariantFor.

function formatRM(cents) {
    return 'RM' + (cents / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function updateDeliveryFeeDisplay() {
    const totalRow = document.querySelector('[data-base-total]');
    if (!totalRow) return;                                    // cart page without the fee lines
    const base = parseInt(totalRow.getAttribute('data-base-total'), 10) || 0;
    const codeEl = document.getElementById('delivery-session-input');
    const code = (codeEl && codeEl.value) || '';
    // TEC-1210: mirror the charging rule exactly — a next-day-onward EVENING is free.
    const dateEl = document.getElementById('delivery-date-input');
    const dateStr = (dateEl && dateEl.value) || '';
    const fee = surchargeVariantFor(code, dateStr) ? (TIMESLOT_SURCHARGE_AMOUNT[code] || 0) : 0;

    const feeRow = document.getElementById('dynamic-delivery-fee-row');
    const feeAmt = document.getElementById('dynamic-delivery-fee-amount');
    if (feeRow) feeRow.style.display = fee > 0 ? '' : 'none';
    if (feeAmt) feeAmt.textContent = formatRM(fee);

    const totalEl = document.getElementById('dynamic-cart-total');
    if (totalEl) totalEl.textContent = formatRM(base + fee);
}

// Reflect the selected slot on load, and once more after the picker restores the
// saved slot (which can run slightly after this deferred script).
updateDeliveryFeeDisplay();
setTimeout(updateDeliveryFeeDisplay, 800);
